/**
 * Course selection over the full catalogue. The app loads every course's books and lesson
 * metadata at once (a few dozen rows), so switching courses is a local filter, not a fetch.
 */

export interface CourseLike {
  id: string;
  lang: string;
}

export interface BookLike {
  id: string;
  course_id: string;
}

export interface LessonLike {
  book_id: string;
}

/** The preferred course if it exists, else the Chinese one, else the first. */
export function chooseCourse<C extends CourseLike>(courses: readonly C[], ...preferred: (string | null)[]): C | null {
  for (const id of preferred) {
    const match = id ? courses.find((c) => c.id === id) : undefined;
    if (match) return match;
  }
  return courses.find((c) => c.lang === 'zh') ?? courses[0] ?? null;
}

/** Books and lessons of one course, in the order they came in. */
export function courseSlice<B extends BookLike, L extends LessonLike>(
  books: readonly B[],
  lessons: readonly L[],
  courseId: string | null,
): { books: B[]; lessons: L[] } {
  if (!courseId) return { books: [], lessons: [] };
  const own = books.filter((b) => b.course_id === courseId);
  const ids = new Set(own.map((b) => b.id));
  return { books: own, lessons: lessons.filter((l) => ids.has(l.book_id)) };
}
